import { useEffect, useRef, useState, useCallback, memo } from "react";
import {
  Grid,
  Stack,
  Typography,
  List,
  Box,
  IconButton,
  ListItemAvatar,
  Avatar,
  Skeleton,
  ListItem,
  Badge,
  useMediaQuery,
  Modal,
  Snackbar,
  Alert
} from "@mui/material";
import { useSelector } from "react-redux";
import { IoAddCircleOutline, IoSearchOutline } from "react-icons/io5";
import { useDispatch } from "react-redux";
import { BsTranslate } from "react-icons/bs";
import { English } from "../../constant/EnglishFallback";
import { MOVE_CHAT_GROUP_MESSAGES_UNREAD_TO_READ } from "../../constant/Apipath";
import { Postrequestcall } from "../../request/Postrequest";
import ChatContainer from "./ChatContainer";
import { setChatCount, setChatState } from "../../redux/actions/actions";
import NewChatModal from "./NewChatModal";
import SelectLanguageModal from "./SelectLanguageModal";
import { getChatgroups, getCurrentChatgroup } from "../../common/Helper";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import SearchField from "./SearchField";
import {
  deleteChatandMessagesFromIndexedDB,
  getChatsFromIndexedDB,
  saveChatsToIndexedDB,
  updateChatInIndexedDB
} from "../../db";
import { setChatGroups, setSelectedChat } from "../../redux/actions/actions";

const ChatListItem = memo(({ item, onChatClick }) => {
  const { selectedChat } = useSelector((state) => state?.main);

  const handleClick = useCallback(() => {
    onChatClick(item);
  }, [item, onChatClick]);

  return (
    <ListItem
      key={item?.chatGroupId}
      onClick={handleClick}
      sx={{
        backgroundColor:
          selectedChat?.chatGroupId === item?.chatGroupId ? "#f7faff" : "transparent",
        borderLeft:
          selectedChat?.chatGroupId === item?.chatGroupId
            ? "4px solid #1976d2"
            : "4px solid transparent",
        boxShadow:
          selectedChat?.chatGroupId === item?.chatGroupId
            ? "0 2px 8px 0 rgba(25, 118, 210, 0.08)"
            : "none",
        pl: selectedChat?.chatGroupId === item?.chatGroupId ? 0.5 : 1.5,
        pr: 1.5,
        py: 1.2,
        cursor: "pointer",
        transition: "background-color 0.2s, border-left 0.2s, box-shadow 0.2s"
      }}>
      <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 1 }}>
        {/* Avatar Section */}
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 52, mr: 1 }}>
          {item?.isGroupchat && item?.nrOfUsersInGroup > 2 ? (
            <Box sx={{ position: "relative", width: 45, height: 45 }}>
              <Avatar
                src={item?.groupUserPictureUrl1}
                sx={{
                  position: "absolute",
                  zIndex: 2,
                  width: 38,
                  height: 38,
                  left: 0,
                  top: 0
                }}
              />
              <Avatar
                src={item?.groupUserPictureUrl2}
                sx={{
                  position: "absolute",
                  left: 20,
                  top: 1,
                  zIndex: 1,
                  width: 30,
                  height: 30
                }}
              />
            </Box>
          ) : (
            <Avatar src={item?.pictureUrl} sx={{ width: 45, height: 45 }} />
          )}
        </Box>
        {/* Text Section */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body1"
            fontWeight={item?.unreadMessagesNr !== 0 ? "bold" : "normal"}
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "200px",
              fontSize: {
                xs: "0.9rem",
                sm: "1rem",
                md: "1.05rem",
                lg: "15px"
              }
            }}>
            {item?.name}
          </Typography>
          <Typography
            variant="body2"
            color={item?.unreadMessagesNr !== 0 ? "black" : "gray"}
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "225px",
              fontSize: {
                xs: "0.75rem",
                sm: "0.8rem",
                md: "0.85rem",
                lg: "0.9rem"
              }
            }}>
            {item?.messageText}
          </Typography>
        </Box>
        {/* Status Section */}
        <Stack sx={{ alignItems: "center", gap: 2, ml: 1, mt: 1 }}>
          {item?.muted && <NotificationsOffIcon fontSize="small" color="disabled" />}
          {item?.unreadMessagesNr !== 0 && (
            <Badge
              badgeContent={item?.unreadMessagesNr > 99 ? "99+" : item?.unreadMessagesNr}
              color="primary"
              sx={{
                "& .MuiBadge-badge": {
                  fontSize: {
                    xs: "0.65rem",
                    sm: "0.75rem",
                    md: "0.85rem"
                  },
                  minWidth: { xs: 18, sm: 20 },
                  height: { xs: 18, sm: 20 }
                }
              }}
            />
          )}
          <Typography
            variant="caption"
            color="gray"
            fontWeight="bold"
            sx={{
              fontSize: {
                xs: "0.65rem",
                sm: "0.7rem",
                md: "0.75rem"
              }
            }}>
            {item?.dateSentDisplay || item?.dateSent}
          </Typography>
        </Stack>
      </Box>
    </ListItem>
  );
});

ChatListItem.displayName = "ChatListItem";

function ChatLayout() {
  const dispatch = useDispatch();

  const {
    multiLanguageData,
    loginData,
    chatState,
    unreadMsgCount,
    chatGroups,
    selectedChat = {}
  } = useSelector((state) => state?.main);

  const selectedChatRef = useRef(0);

  const LanguageData = multiLanguageData?.Chat || {};
  const {
    TEXT_MESSAGES = English.Chat.TEXT_MESSAGES,
    SEARCH_CONVERSATIONS = English.Chat.SEARCH_CONVERSATIONS,
    WELCOME_TO_MESSAGES = English.Chat.WELCOME_TO_MESSAGES,
    TEXT_CHAT_WITH_YOUR_CONTACT = English.Chat.TEXT_CHAT_WITH_YOUR_CONTACT,
    TEXT_START_A_NEW_CHAT = English.Chat.TEXT_START_A_NEW_CHAT,
    TEXT_PLACEHOLDER_NO_DATA = English.Chat.TEXT_PLACEHOLDER_NO_DATA
  } = LanguageData || {};

  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [openLangModal, setOpenLangModal] = useState(false);
  const [openNewChatModal, setOpenNewChatModal] = useState(false);
  const [languageData, setLanguageData] = useState({
    languageName: "",
    languageCode: ""
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const [messageLoader, setMessageLoader] = useState(true);

  useEffect(() => {
    selectedChatRef.current = selectedChat || null;
  }, [selectedChat]);

  // Get the users or groups list
  useEffect(() => {
    getGroupList(chatState?.chatGroupId || null);
  }, [chatState?.refresh]);

  useEffect(() => {
    // Cleanup when leaving the chat route
    return () => {
      dispatch(setChatState({}));
      dispatch(setSelectedChat({}));
    };
  }, []);

  // Mark messages as read when unmounting (user navigates away, refreshes, or closes page)
  useEffect(() => {
    const markMessagesAsRead = () => {
      const currentChatGroupId = selectedChatRef.current?.chatGroupId;
      const lastMessageId =
        selectedChatRef.current?.lastMessageId || selectedChatRef.current?.messageId || 0;
      if (currentChatGroupId && loginData?.userInformation?.UserId && loginData?.accessToken) {
        const payload = {
          UserId: loginData.userInformation.UserId,
          ChatGroupId: currentChatGroupId,
          LastKnownMessageId: lastMessageId
        };
        const apiUrl = `${import.meta.env.REACT_APP_BASE_URL}/${MOVE_CHAT_GROUP_MESSAGES_UNREAD_TO_READ}`;
        // fetch with keepalive flag for reliable delivery during page unload
        fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${loginData.accessToken}`
          },
          body: JSON.stringify(payload),
          keepalive: true // Ensures request completes even if page is closing
        }).catch((e) => console.error("Error marking messages as read:", e));
      }
    };
    // Handle page refresh and browser close
    const handleBeforeUnload = (e) => {
      markMessagesAsRead();
      // Clear chat state on page refresh
      dispatch(
        setChatState({
          messageLink: "",
          messageText: "",
          chatGroupId: null,
          from: ""
        })
      );
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    // Handle component unmount (navigation to other routes)
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      markMessagesAsRead();
    };
  }, []);

  const getGroupList = async (chatGroupId) => {
    try {
      setLoading(true);
      const cachedChats = await getChatsFromIndexedDB(); // 1. Try to load from IndexedDB first (instant load)
      if (cachedChats && cachedChats?.length > 0) {
        let ifChatgroupPresent = cachedChats.find((chat) => chat.chatGroupId == chatGroupId);
        if (chatGroupId !== null && !ifChatgroupPresent) {
          let getNewChatgroup = await getCurrentChatgroup(chatGroupId, loginData);
          if (getNewChatgroup) {
            cachedChats.unshift({
              ...getNewChatgroup
            });
            await saveChatsToIndexedDB(cachedChats); // Store new chat in IndexedDB
            await showChatDetails(cachedChats, chatGroupId);
          } else {
            await showChatDetails(cachedChats, null);
          }
        } else {
          await showChatDetails(cachedChats, chatGroupId);
        }
      } else {
        // 2. No cache available, fetch from API (first time or after logout)
        let getGroupList = await getChatgroups(dispatch, loginData);
        if (getGroupList && getGroupList?.length > 0) {
          // Store in IndexedDB for next time
          await saveChatsToIndexedDB(getGroupList);
          await showChatDetails(getGroupList, chatGroupId);
        } else {
          dispatch(setChatGroups([]));
        }
      }
      setLoading(false);
    } catch (error) {
      console.log("Get Group List API Error: ", error);
      setLoading(false);
    } finally {
      setMessageLoader(false);
    }
  };

  const showChatDetails = async (getChatgroups, chatGroupId) => {
    if (chatGroupId !== null && chatState?.from !== "newChat") {
      // If we came from notification like foreground or background or from profile or builder network,carpool,buysell then the current chat group count should be zero
      let getChatgroup = getChatgroups.find((group) => group?.chatGroupId === chatGroupId);

      // Update UI state first for immediate feedback
      if (unreadMsgCount > 0 && getChatgroup?.unreadMessagesNr > 0) {
        const remaining = Math.max(0, unreadMsgCount - getChatgroup?.unreadMessagesNr);
        dispatch(setChatCount(remaining));
      }
      const newPayload = {
        UserId: loginData?.userInformation?.UserId,
        ChatGroupId: getChatgroup?.chatGroupId,
        LastKnownMessageId: 0 // Reset to 0 for new chat
      };
      await Postrequestcall(
        MOVE_CHAT_GROUP_MESSAGES_UNREAD_TO_READ,
        newPayload,
        loginData?.accessToken
      );
      // ✅ UPDATE INDEXEDDB: Mark chat as read
      await updateChatInIndexedDB(chatGroupId, { unreadMessagesNr: 0 });
      // Update local state Mark chat as read
      let getList = getChatgroups.map((group) =>
        group.chatGroupId === chatGroupId ? { ...group, unreadMessagesNr: 0 } : group
      );
      dispatch(setChatGroups(getList));
      showCurrentChatGroupDetails(getList, chatGroupId);
    } else {
      if (chatState?.from === "newChat") {
        showCurrentChatGroupDetails(getChatgroups, chatGroupId);
      } else {
        dispatch(setSelectedChat({}));
      }
      dispatch(setChatGroups([...getChatgroups]));
    }
  };

  const showCurrentChatGroupDetails = (getChatgroups, chatGroupId) => {
    // Show details if chatGroupId is provided
    const selectedChatDetails = getChatgroups?.find((chat) => chat?.chatGroupId === chatGroupId);
    selectedChatDetails && dispatch(setSelectedChat(selectedChatDetails || {}));
  };

  const onChatClick = useCallback(
    async (item) => {
      // Get previous chat details before updating state
      const prevChatGroupId = selectedChat?.chatGroupId;
      const prevLastMessageId = selectedChat?.lastMessageId || selectedChat?.messageId || 0;
      // Update UI state first for immediate feedback
      const remaining = Math.max(0, unreadMsgCount - item.unreadMessagesNr);
      dispatch(setChatCount(remaining));
      let prevChatGroups = chatGroups.map((chatItem) =>
        chatItem.chatGroupId === item.chatGroupId ? { ...chatItem, unreadMessagesNr: 0 } : chatItem
      );
      dispatch(setChatGroups(prevChatGroups));
      dispatch(setSelectedChat(item));
      dispatch(
        setChatState({
          ...chatState,
          messageLink: "",
          messageText: "",
          chatGroupId: null,
          from: ""
        })
      );
      // ✅ UPDATE INDEXEDDB: Mark chat as read
      await updateChatInIndexedDB(item.chatGroupId, { unreadMessagesNr: 0 });
      // Mark BOTH chats as read in parallel
      const markAsReadPromises = [];
      // 1. Mark PREVIOUS chat as read (if exists and different from new chat)
      if (prevChatGroupId && prevChatGroupId !== item.chatGroupId) {
        const prevPayload = {
          UserId: loginData?.userInformation?.UserId,
          ChatGroupId: prevChatGroupId,
          LastKnownMessageId: prevLastMessageId
        };
        markAsReadPromises.push(
          Postrequestcall(
            MOVE_CHAT_GROUP_MESSAGES_UNREAD_TO_READ,
            prevPayload,
            loginData?.accessToken
          ).catch((e) => console.error("Error marking previous chat as read:", e))
        );
      }
      // 2. Mark NEW chat as read (reset unread count)
      const newPayload = {
        UserId: loginData?.userInformation?.UserId,
        ChatGroupId: item.chatGroupId,
        LastKnownMessageId: 0 // Reset to 0 for new chat
      };
      markAsReadPromises.push(
        Postrequestcall(
          MOVE_CHAT_GROUP_MESSAGES_UNREAD_TO_READ,
          newPayload,
          loginData?.accessToken
        ).catch((e) => console.error("Error marking new chat as read:", e))
      );
      // Execute both API calls in parallel (fire and forget)
      Promise.allSettled(markAsReadPromises).catch((e) => {
        console.error("Error in mark as read calls:", e);
      });
    },
    [chatGroups, unreadMsgCount, dispatch, loginData]
  );

  const updateChatList = async (status, chatGroupId, msg, from) => {
    let updatedList = chatGroups;
    if (from === "mute-unmute") {
      updatedList = chatGroups.map((chat) =>
        chat.chatGroupId === chatGroupId ? { ...chat, muted: status } : chat
      );
      // ✅ UPDATE INDEXEDDB: Update mute status
      await updateChatInIndexedDB(chatGroupId, { muted: status });
    } else if (from === "leave-chat") {
      // It's for leave chat not for delete chat
      updatedList = chatGroups.filter((chat) => chat.chatGroupId !== chatGroupId);
      // ✅ UPDATE INDEXEDDB: Remove chat and its messages
      await deleteChatandMessagesFromIndexedDB(chatGroupId);
    }
    if (from === "mute-unmute" || from === "leave-chat") {
      dispatch(setChatGroups([...updatedList]));
    }
    setSnackbarMessage(msg);
    setSnackbarOpen(true);
  };

  const filteredChatGroups = chatGroups?.filter((chat) => {
    const searchTerm = searchQuery.toLowerCase();
    return chat?.name?.toLowerCase().includes(searchTerm) || "";
  });

  // Handler for closing the mobile chat modal
  const handleCloseMobileChat = useCallback(() => {
    dispatch(setSelectedChat({}));
  }, [dispatch]);

  return (
    <>
      <Grid container className="chat-container">
        <Grid size={{ xs: 12, sm: 12, md: 3, lg: 3, xl: 3 }} className="chat-list">
          <Box p={1}>
            <Stack direction="row" spacing={1} display="flex" justifyContent="space-between" mb={2}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <ListItemAvatar>
                  <Avatar src={loginData?.pictureUrl} sx={{ width: 45, height: 45 }} />
                </ListItemAvatar>
                <Typography variant="h6" fontWeight="bold">
                  {TEXT_MESSAGES}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                <BsTranslate
                  className="text-xl cursor-pointer"
                  height={18}
                  cursor={"pointer"}
                  width={18}
                  onClick={() => setOpenLangModal(true)}
                />
                <IconButton
                  color="primary"
                  aria-label="create new chat"
                  onClick={() => {
                    dispatch(setChatState(null));
                    setOpenNewChatModal(true);
                  }}>
                  <IoAddCircleOutline size={24} />
                </IconButton>
              </Box>
            </Stack>
            <SearchField
              value={searchQuery}
              onChange={(e) => {
                let inputValue = e.target.value;
                inputValue = inputValue.replace(/^[^A-Za-z0-9]+/, "");
                setSearchQuery(inputValue);
              }}
              placeholder={SEARCH_CONVERSATIONS}
            />
          </Box>
          <Stack className="chat-list-scroll">
            <List>
              {loading ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    width: "100%",
                    padding: "5px"
                  }}>
                  {Array.from({ length: 10 }).map((_, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        padding: "5px"
                      }}>
                      <Skeleton variant="circular" width={45} height={45} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="80%" height={20} />
                        <Skeleton variant="text" width="60%" height={16} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : filteredChatGroups?.length !== 0 ? (
                filteredChatGroups?.map((item) => (
                  <ChatListItem key={item?.chatGroupId} item={item} onChatClick={onChatClick} />
                ))
              ) : (
                <Typography variant="h6" align="center" sx={{ color: "gray", mt: 2 }}>
                  {TEXT_PLACEHOLDER_NO_DATA}
                </Typography>
              )}
            </List>
          </Stack>
        </Grid>
        {!isSmallScreen && (
          <Grid size={{ xs: 12, sm: 12, md: 9, lg: 9, xl: 9 }} className="chat-screen">
            {typeof selectedChat === "object" && Object.keys(selectedChat)?.length === 0 && (
              <Box
                display="flex"
                flexDirection="column"
                textAlign="center"
                container
                justifyContent="center"
                alignItems="center"
                sx={{
                  height: "100vh"
                }}>
                <Avatar
                  sx={{
                    width: 120,
                    height: 120,
                    mb: 3,
                    backgroundColor: "primary.light"
                  }}>
                  <IoSearchOutline size={60} />
                </Avatar>
                <Typography variant="h4" color="primary" gutterBottom fontWeight="bold">
                  {WELCOME_TO_MESSAGES}
                </Typography>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {TEXT_CHAT_WITH_YOUR_CONTACT}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                  {TEXT_START_A_NEW_CHAT}
                </Typography>
              </Box>
            )}
            {/* Desktop: Inline ChatContainer */}
            {typeof selectedChat === "object" && Object.keys(selectedChat)?.length !== 0 && (
              <ChatContainer
                updateChatList={updateChatList}
                messageLoader={messageLoader}
                setMessageLoader={setMessageLoader}
                isGroupListLoading={loading}
              />
            )}
          </Grid>
        )}
        {/* Mobile: ChatContainer inside modal */}
        {isSmallScreen &&
          typeof selectedChat === "object" &&
          Object.keys(selectedChat)?.length !== 0 && (
            <Modal
              open={Object.keys(selectedChat)?.length !== 0}
              onClose={handleCloseMobileChat}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1300
              }}>
              <Box
                sx={{
                  width: "95%",
                  height: "98%",
                  bgcolor: "background.paper",
                  outline: "none",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 3,
                  boxShadow: 24,
                  position: "relative",
                  overflow: "hidden"
                }}>
                <ChatContainer
                  updateChatList={updateChatList}
                  setMessageLoader={setMessageLoader}
                  isGroupListLoading={loading}
                />
              </Box>
            </Modal>
          )}
      </Grid>
      {/* This will prevent model to mount every time */}
      {openLangModal && (
        <SelectLanguageModal
          open={openLangModal}
          languageData={languageData}
          setLanguageData={setLanguageData}
          getData={async () => await getGroupList(selectedChat?.chatGroupId ?? null)}
          onClose={() => {
            setOpenLangModal(false);
          }}
        />
      )}
      {openNewChatModal && (
        <NewChatModal
          open={openNewChatModal}
          from={"newChat"}
          onClose={() => setOpenNewChatModal(false)}
          setMessageLoader={setMessageLoader}
        />
      )}
      {snackbarOpen && (
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={(event, reason) => {
            if (reason === "clickaway") return;
            setSnackbarOpen(false);
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity="success"
            sx={{ width: "100%" }}
            variant="filled">
            {snackbarMessage}
          </Alert>
        </Snackbar>
      )}
    </>
  );
}

export default memo(ChatLayout);
